import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Package, Plus, Search, FileDown, CheckCircle2, Paperclip, Image as ImageIcon, Mic, Video, Trash2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSortableTable, SortableTh } from "@/components/SortableTable";

// ---- Constants ----
// Categories are user-facing labels; the backend uses the exact same strings.
const CATEGORIES = ["Fabric", "Pdt Trims", "Pkg Trims"];
const CATEGORY_ACCENT = {
  Fabric:      { chip: "bg-purple-100 text-purple-800 border-purple-200",  head: "from-purple-500 to-fuchsia-500" },
  "Pdt Trims": { chip: "bg-amber-100 text-amber-800 border-amber-200",     head: "from-amber-500 to-orange-500" },
  "Pkg Trims": { chip: "bg-teal-100 text-teal-800 border-teal-200",        head: "from-teal-500 to-cyan-500" },
};
// Media limits mirror the backend (kept in sync manually — small enough not to warrant an endpoint).
const MEDIA_LIMITS = { photo: 10, audio: 25, video: 50 }; // MB
const MEDIA_ICON = { photo: ImageIcon, audio: Mic, video: Video };

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// Days between `iso` and now, floored. Used for the ageing badge on Open requests.
const daysSince = (iso) => {
  if (!iso) return 0;
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return 0;
    return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  } catch { return 0; }
};

const kindForMime = (mime) => {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "photo";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("video/")) return "video";
  return null;
};

// ---- Category head card (dashboard) ----
function CategoryHead({ category, stats, staleDays, onOpen, activeCategory }) {
  const isActive = activeCategory === category;
  const accent = CATEGORY_ACCENT[category] || CATEGORY_ACCENT.Fabric;
  const stale = stats?.stale || 0;
  return (
    <button
      type="button"
      onClick={() => onOpen(category)}
      data-testid={`ftr-head-${category.toLowerCase().replace(/ /g, "-")}`}
      className={`text-left rounded-lg border transition-all overflow-hidden ${isActive ? "border-[#7C1D3A] ring-2 ring-[#7C1D3A]/30 shadow-md" : "border-slate-200 hover:border-[#7C1D3A]/40 hover:shadow-sm"}`}
    >
      <div className={`h-1.5 bg-gradient-to-r ${accent.head}`} />
      <div className="p-4 bg-white">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-800">{category}</div>
          </div>
          {stale > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 animate-pulse"
              title={`${stale} Open request(s) older than ${staleDays} days`}
              data-testid={`ftr-head-stale-${category.toLowerCase().replace(/ /g, "-")}`}
            >
              <AlertTriangle className="h-3 w-3" /> {stale} stale
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Open</div>
            <div className="text-2xl font-bold text-[#7C1D3A]" data-testid={`ftr-head-open-${category.toLowerCase().replace(/ /g, "-")}`}>{stats?.open ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Completed</div>
            <div className="text-2xl font-bold text-emerald-700">{stats?.completed ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Total</div>
            <div className="text-2xl font-bold text-slate-800">{stats?.total ?? 0}</div>
          </div>
        </div>
      </div>
    </button>
  );
}

// ---- New Request dialog ----
function NewRequestDialog({ open, onClose, defaultCategory, buyers, onCreated }) {
  const [form, setForm] = useState({
    category: defaultCategory || "Fabric",
    im_code: "", buyer: "", supplier: "", supplier_code: "", description: "", reason: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, category: defaultCategory || "Fabric", im_code: "", buyer: "", supplier: "", supplier_code: "", description: "", reason: "" }));
  }, [open, defaultCategory]);

  const submit = async () => {
    // Client-side reason gate. Server enforces the same rule; we show the exact
    // popup message the spec asks for.
    if (!form.reason.trim()) {
      window.alert("request reason is mandatory");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ftr/requests", form);
      toast.success(`Request ${data.request_number} raised.`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to raise request";
      if (msg.toLowerCase().includes("reason")) window.alert("request reason is mandatory");
      else toast.error(msg);
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ftr-new-dialog">
        <DialogHeader>
          <DialogTitle>Raise a Fabric / Trim Request</DialogTitle>
          <DialogDescription className="text-[12px]">A notification, email and WhatsApp will fire to the configured recipients for the selected category.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Category *</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ftr-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">IM Code</label>
            <Input value={form.im_code} onChange={(e) => setForm({ ...form, im_code: e.target.value })} placeholder="e.g. IM-1042" data-testid="ftr-form-im-code" />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Buyer *</label>
            <Select value={form.buyer} onValueChange={(v) => setForm({ ...form, buyer: v })}>
              <SelectTrigger data-testid="ftr-form-buyer"><SelectValue placeholder="Select buyer…" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {(buyers || []).map((b) => <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Supplier</label>
            <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="e.g. Arvind Mills" data-testid="ftr-form-supplier" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Supplier Code / Details</label>
            <Input value={form.supplier_code} onChange={(e) => setForm({ ...form, supplier_code: e.target.value })} placeholder="e.g. ARV-CV-2001, contact person, cell #…" data-testid="ftr-form-supplier-code" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 200 GSM CVC single jersey, ecru" data-testid="ftr-form-description" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">
              Reason for request <span className="text-red-600">*</span>
            </label>
            <Textarea rows={4} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Explain why this material is needed — sampling, shortfall, replacement…" data-testid="ftr-form-reason" />
            <div className="text-[10.5px] text-slate-500 mt-1">Mandatory. A popup will block submission until this field is filled.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="ftr-form-submit">
            {busy ? "Raising…" : "Raise request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Request Detail dialog ----
function RequestDetailDialog({ request, onClose, canClose, onChanged }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const media = request?.media || [];
  const isCompleted = request?.status === "Completed";

  const pickFile = (kind) => {
    if (!canClose) { toast.error("Only Stores/Production can attach media"); return; }
    const el = fileRef.current;
    if (!el) return;
    el.accept = kind === "photo" ? "image/*" : kind === "audio" ? "audio/*" : "video/*";
    el.dataset.kind = kind;
    el.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const kind = kindForMime(file.type);
    if (!kind) { toast.error("Only photos, audio and video are supported"); return; }
    const limitMb = MEDIA_LIMITS[kind];
    if (file.size > limitMb * 1024 * 1024) { toast.error(`${kind.charAt(0).toUpperCase() + kind.slice(1)} exceeds ${limitMb} MB limit`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/ftr/requests/${request.id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Attached ${kind}: ${file.name}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const deleteMedia = async (mid, name) => {
    if (!window.confirm(`Remove '${name}' from this request?`)) return;
    try {
      await api.delete(`/ftr/requests/${request.id}/media/${mid}`);
      toast.success("Media removed");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const complete = async () => {
    if (!window.confirm(`Close request ${request.request_number}?\n\nEmail + WhatsApp will fire to configured recipients.`)) return;
    setBusy(true);
    try {
      await api.post(`/ftr/requests/${request.id}/complete`, { completion_notes: notes.trim() });
      toast.success(`Request ${request.request_number} completed.`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to complete"); }
    setBusy(false);
  };

  const downloadPdf = async (kind) => {
    try {
      const res = await api.get(`/ftr/requests/${request.id}/pdf/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `FTR_${request.request_number}_${kind}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not download PDF"); }
  };

  const downloadMedia = async (m) => {
    try {
      const res = await api.get(`/ftr/requests/${request.id}/media/${m.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: m.mime || "application/octet-stream" }));
      const a = document.createElement("a"); a.href = url; a.download = m.filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  if (!request) return null;
  const accent = CATEGORY_ACCENT[request.category] || CATEGORY_ACCENT.Fabric;

  return (
    <Dialog open={!!request} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="ftr-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>Request {request.request_number}</span>
            <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border ${accent.chip}`}>{request.category}</span>
            <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border ${isCompleted ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{request.status}</span>
          </DialogTitle>
          <DialogDescription className="text-[12px]">Raised by {request.raised_by_name || request.raised_by} · {fmtDateTime(request.raised_at)}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <div><div className="text-[10.5px] uppercase text-slate-500 font-semibold">IM Code</div><div>{request.im_code || "—"}</div></div>
          <div><div className="text-[10.5px] uppercase text-slate-500 font-semibold">Buyer</div><div>{request.buyer || "—"}</div></div>
          <div><div className="text-[10.5px] uppercase text-slate-500 font-semibold">Supplier</div><div>{request.supplier || "—"}</div></div>
          <div><div className="text-[10.5px] uppercase text-slate-500 font-semibold">Supplier Code</div><div>{request.supplier_code || "—"}</div></div>
          <div className="sm:col-span-2"><div className="text-[10.5px] uppercase text-slate-500 font-semibold">Description</div><div className="whitespace-pre-wrap">{request.description || "—"}</div></div>
          <div className="sm:col-span-2">
            <div className="text-[10.5px] uppercase text-slate-500 font-semibold">Reason</div>
            <div className="whitespace-pre-wrap p-2 rounded bg-amber-50 border border-amber-100">{request.reason || "—"}</div>
          </div>
          {isCompleted && (
            <div className="sm:col-span-2">
              <div className="text-[10.5px] uppercase text-slate-500 font-semibold">Completion notes</div>
              <div className="whitespace-pre-wrap p-2 rounded bg-emerald-50 border border-emerald-100">{request.completion_notes || "—"}</div>
              <div className="text-[11px] text-slate-500 mt-1">Closed by {request.completed_by_name || request.completed_by} · {fmtDateTime(request.completed_at)}</div>
            </div>
          )}
        </div>

        {/* Media section */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-700">Media ({media.length})</div>
              <div className="text-[11px] text-slate-500">Photo up to {MEDIA_LIMITS.photo} MB · Audio {MEDIA_LIMITS.audio} MB · Video {MEDIA_LIMITS.video} MB</div>
            </div>
            {canClose && (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => pickFile("photo")} disabled={uploading} data-testid="ftr-media-attach-photo"><ImageIcon className="h-3.5 w-3.5 mr-1" /> Photo</Button>
                <Button size="sm" variant="outline" onClick={() => pickFile("audio")} disabled={uploading} data-testid="ftr-media-attach-audio"><Mic className="h-3.5 w-3.5 mr-1" /> Audio</Button>
                <Button size="sm" variant="outline" onClick={() => pickFile("video")} disabled={uploading} data-testid="ftr-media-attach-video"><Video className="h-3.5 w-3.5 mr-1" /> Video</Button>
              </div>
            )}
            <input ref={fileRef} type="file" className="hidden" onChange={onFileChosen} />
          </div>
          {media.length === 0 ? (
            <div className="text-[12px] text-slate-500 italic mt-2">No media attached yet.</div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {media.map((m) => {
                const Icon = MEDIA_ICON[m.kind] || Paperclip;
                return (
                  <div key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-slate-50 text-[12.5px]">
                    <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                    <button type="button" onClick={() => downloadMedia(m)} className="truncate flex-1 text-left hover:underline text-[#7C1D3A]" title={m.filename}>{m.filename}</button>
                    <span className="text-[10.5px] text-slate-500 font-mono shrink-0">{Math.max(1, Math.round((m.size || 0) / 1024))} KB</span>
                    {canClose && !isCompleted && (
                      <button type="button" onClick={() => deleteMedia(m.id, m.filename)} className="text-red-600 hover:text-red-800 shrink-0" title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {media.length > 0 && isCompleted && (
            <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2.5 leading-relaxed">
              <b>ALERT:</b> This completed request has media attached — the closure PDF includes this alert too. Reviewers should open each file before signing off.
            </div>
          )}
        </div>

        {/* Complete section (only when Open + user can close) */}
        {!isCompleted && canClose && (
          <div className="mt-4 border-t pt-4">
            <label className="text-[10.5px] uppercase tracking-widest text-slate-600 font-semibold">Completion notes</label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done, from where, delivered to whom…" data-testid="ftr-complete-notes" />
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" onClick={() => downloadPdf("raised")} data-testid="ftr-pdf-raised"><FileDown className="h-3.5 w-3.5 mr-1" /> Raised PDF</Button>
            {isCompleted && <Button variant="outline" size="sm" onClick={() => downloadPdf("completed")} data-testid="ftr-pdf-completed"><FileDown className="h-3.5 w-3.5 mr-1" /> Completed PDF</Button>}
          </div>
          <div className="flex gap-1.5">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            {!isCompleted && canClose && (
              <Button onClick={complete} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ftr-complete-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> {busy ? "Closing…" : "Mark completed"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Main page ----
export default function FabricTrimRequest() {
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const caps = useMemo(() => new Set(user?.capabilities || []), [user]);
  const isAdmin = user?.role === "admin" || user?.role === "admin_addl";
  const canRaise = isAdmin || caps.has("raise_ftr_request");
  const canClose = isAdmin || caps.has("close_ftr_request");

  const [stats, setStats] = useState({});
  // staleDays is fed from GET /ftr/dashboard._meta.stale_days — server-controlled so a
  // future admin knob (e.g. per-tenant SLA) can flip the threshold without a client change.
  const [staleDays, setStaleDays] = useState(3);
  const [requests, setRequests] = useState([]);
  const [category, setCategory] = useState("Fabric");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [buyers, setBuyers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openReq, setOpenReq] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, list, byrs] = await Promise.all([
        api.get("/ftr/dashboard"),
        api.get("/ftr/requests", { params: { category } }),
        api.get("/masters/buyers"),
      ]);
      setStats(dash.data || {});
      setStaleDays(dash.data?._meta?.stale_days ?? 3);
      setRequests(list.data || []);
      setBuyers(byrs.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Fabric/Trim requests");
    }
    setLoading(false);
  }, [category]);

  useEffect(() => { reload(); }, [reload]);

  // Open the route-linked request in a modal on first load (deep-link support).
  useEffect(() => {
    if (!routeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/ftr/requests/${routeId}`);
        setCategory(data.category || "Fabric");
        setOpenReq(data);
      } catch { /* invalid link – silently ignore */ }
    })();
  }, [routeId]);

  const refreshOpenReq = async () => {
    if (openReq) {
      try { const { data } = await api.get(`/ftr/requests/${openReq.id}`); setOpenReq(data); } catch { /* noop */ }
    }
    reload();
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (requests || []).filter((r) => {
      if (statusFilter === "Stale") {
        if (r.status !== "Open") return false;
        if (daysSince(r.raised_at) < staleDays) return false;
      } else if (statusFilter !== "all" && r.status !== statusFilter) {
        return false;
      }
      if (!term) return true;
      const hay = `${r.request_number} ${r.buyer} ${r.im_code} ${r.supplier} ${r.description} ${r.reason}`.toLowerCase();
      return hay.includes(term);
    });
  }, [requests, q, statusFilter, staleDays]);

  const staleCountInCategory = useMemo(
    () => (requests || []).filter((r) => r.status === "Open" && daysSince(r.raised_at) >= staleDays).length,
    [requests, staleDays],
  );

  const ftrColumns = useMemo(() => ({
    ref:       { get: (r) => r.request_number || "", type: "text" },
    buyer:     { get: (r) => r.buyer || "", type: "text" },
    im_code:   { get: (r) => r.im_code || "", type: "text" },
    desc:      { get: (r) => r.description || "", type: "text" },
    raised_by: { get: (r) => r.raised_by_name || r.raised_by || "", type: "text" },
    raised_at: { get: (r) => r.raised_at || "", type: "date" },
    age:       { get: (r) => (r.status === "Open" ? daysSince(r.raised_at) : -1), type: "number" },
    media:     { get: (r) => (r.media || []).length, type: "number" },
    status:    { get: (r) => r.status || "", type: "text" },
  }), []);
  const { sortedRows: ftrRows, sortKey: fSortKey, sortDir: fSortDir, cycleSort: fCycle } = useSortableTable(filtered, ftrColumns, { defaultKey: "raised_at", defaultDir: "desc", storageKey: "sort:opsiq:ftr" });

  return (
    <div className="p-6 space-y-6" data-testid="ftr-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Operations</div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-2"><Package className="h-6 w-6 text-[#7C1D3A]" /> Fabric / Trim Request</h1>
          <div className="text-[12.5px] text-slate-500">Raise a request against Fabric, Production Trims or Package Trims. Stores / Production close it out with completion notes and any supporting media.</div>
        </div>
        {canRaise && (
          <Button className="bg-brand hover:bg-brand-dark text-white" onClick={() => setOpenNew(true)} data-testid="ftr-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New request
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {CATEGORIES.map((c) => (
          <CategoryHead key={c} category={c} stats={stats[c]} staleDays={staleDays} onOpen={setCategory} activeCategory={category} />
        ))}
      </div>

      <Card className="p-4 border-slate-200 rounded-md bg-white">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13px] font-semibold text-slate-800">{category} requests</div>
            <div className="flex gap-1 flex-wrap">
              {["all", "Open", "Completed", "Stale"].map((s) => {
                const isStale = s === "Stale";
                const active = statusFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-2 py-1 text-[11px] rounded border inline-flex items-center gap-1 ${active ? (isStale ? "bg-red-600 text-white border-red-600" : "bg-[#7C1D3A] text-white border-[#7C1D3A]") : (isStale ? "bg-white text-red-700 border-red-200 hover:border-red-400" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]/40")}`}
                    data-testid={`ftr-filter-${s.toLowerCase()}`}
                  >
                    {isStale && <AlertTriangle className="h-3 w-3" />}
                    {s === "all" ? "All" : s}
                    {isStale && staleCountInCategory > 0 && (
                      <span className={`text-[9.5px] font-mono px-1 rounded ${active ? "bg-white/20" : "bg-red-100 text-red-800"}`}>{staleCountInCategory}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref / buyer / IM / supplier…" className="pl-7 h-8 w-72" data-testid="ftr-search" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <SortableTh label="Ref" colKey="ref" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Buyer" colKey="buyer" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="IM Code" colKey="im_code" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Description" colKey="desc" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Raised by" colKey="raised_by" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Raised at" colKey="raised_at" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Age" colKey="age" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Media" colKey="media" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
                <SortableTh label="Status" colKey="status" sortKey={fSortKey} sortDir={fSortDir} onClick={fCycle} className="py-2 px-2" />
              </tr>
            </thead>
            <tbody>
              {ftrRows.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500 italic">{loading ? "Loading…" : "No requests found."}</td></tr>
              )}
              {ftrRows.map((r) => {
                const isOpen = r.status === "Open";
                const age = daysSince(r.raised_at);
                const isStale = isOpen && age >= staleDays;
                return (
                <tr
                  key={r.id}
                  onClick={() => setOpenReq(r)}
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isStale ? "bg-red-50/40" : ""}`}
                  data-testid={`ftr-row-${r.request_number}`}
                >
                  <td className="py-2 px-2 font-mono text-[#7C1D3A] font-semibold">{r.request_number}</td>
                  <td className="py-2 px-2">{r.buyer || "—"}</td>
                  <td className="py-2 px-2 font-mono">{r.im_code || "—"}</td>
                  <td className="py-2 px-2 max-w-xs truncate" title={r.description}>{r.description || "—"}</td>
                  <td className="py-2 px-2">{r.raised_by_name || r.raised_by}</td>
                  <td className="py-2 px-2 text-slate-600">{fmtDateTime(r.raised_at)}</td>
                  <td className="py-2 px-2">
                    {isOpen ? (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-mono ${isStale ? "bg-red-100 text-red-700 border border-red-200 font-semibold" : "bg-slate-100 text-slate-600"}`}
                        title={isStale ? `Stale — older than ${staleDays} days` : ""}
                      >
                        {isStale && <AlertTriangle className="h-3 w-3" />}
                        {age}d
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    {(r.media || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-[10.5px] font-mono text-slate-700">
                        <Paperclip className="h-3 w-3" /> {r.media.length}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-2 px-2">
                    <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border ${r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{r.status}</span>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </Card>

      <NewRequestDialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        defaultCategory={category}
        buyers={buyers}
        onCreated={() => reload()}
      />
      <RequestDetailDialog
        request={openReq}
        onClose={() => { setOpenReq(null); if (routeId) nav("/fabric-trim", { replace: true }); }}
        canClose={canClose}
        onChanged={refreshOpenReq}
      />
    </div>
  );
}
